// #355: what the simulator reads off every prescribed set of the cycle: the
// rep and effort distributions, the AMRAP sets, and how long each day lasts.
// Pure; the entry point is simulateProgram (programSimulation.ts).
import type { Exercise } from "./schemas/exercise.js";
import type { AmrapStop } from "./schemas/program.js";
import type { PlannedSet, PlannedTargetWeight } from "./schemas/plan.js";
import type { ResolvedSession } from "./programResolution.js";
import { effectiveRir } from "./effortScale.js";
import { amrapStopRirOf } from "./programPrescription.js";
import { HOLD_FALLBACK_SEC, restSecondsFor } from "./restTimer.js";

/** Seconds one rep takes, for the length of a day. An assumption, and the
 *  output says so: the ACSM position stand (2009, Med Sci Sports Exerc
 *  41:687-708) calls 1-2 s concentric and 1-2 s eccentric a moderate
 *  velocity; 3 s is the middle of that. */
export const SIMULATION_SECONDS_PER_REP = 3;
export const SIMULATION_REP_TIME_SOURCE = "acsm:2009:moderate-velocity";

export type RepsDistribution = {
  /** Sets by prescribed reps, an AMRAP at its floor. Ascending reps. */
  byReps: Array<{ reps: number; sets: number }>;
  /** Of those, the AMRAP floors. */
  amrap: number;
  /** Sets bounded by a clock, not by reps. */
  timed: number;
  /** Sets with 0 reps and no clock: the athlete writes them in session. */
  open: number;
};

export type EffortDistribution = {
  /** Sets with an effort target, as canonical RIR. Ascending RIR. */
  byRir: Array<{ rir: number; sets: number }>;
  /** Sets with no effort target, by what sets their load. */
  untargeted: Array<{ axis: PlannedTargetWeight["kind"]; sets: number }>;
};

export type SimulatedAmrap = {
  weekIdx: number;
  sessionIdx: number;
  exerciseId: string;
  setIdx: number;
  floorReps: number;
  load: PlannedTargetWeight;
  /** The effort target beside it, as RIR; null when there is none. */
  targetRir: number | null;
  /** null: the program writes no stop rule for it. */
  stop: AmrapStop | null;
};

export type SimulatedDay = {
  sessionIdx: number;
  sessionId: string;
  sessionName: string;
  /** Each round the day has sets in, with its estimated seconds.
   *  `untimedSets` is that round's count of sets whose work time cannot be
   *  estimated (an open bout, 0 reps): their rest is counted, their work is
   *  not. */
  weeks: Array<{ weekIdx: number; seconds: number; untimedSets: number }>;
  minSeconds: number;
  maxSeconds: number;
};

const bump = <K>(m: Map<K, number>, k: K): void => {
  m.set(k, (m.get(k) ?? 0) + 1);
};

const sortedPairs = <K extends number | string>(m: Map<K, number>): Array<[K, number]> =>
  [...m.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

export const repsAndEffort = (
  sessions: ReadonlyArray<ResolvedSession>,
): { reps: RepsDistribution; effort: EffortDistribution } => {
  const byReps = new Map<number, number>();
  const byRir = new Map<number, number>();
  const untargeted = new Map<PlannedTargetWeight["kind"], number>();
  let amrap = 0;
  let timed = 0;
  let open = 0;
  for (const session of sessions) {
    for (const entry of session.entries) {
      for (const s of entry.sets) {
        if (s.work !== undefined) timed += 1;
        else if (s.targetReps > 0) {
          bump(byReps, s.targetReps);
          if (s.amrap === true) amrap += 1;
        } else open += 1;
        const rir = effectiveRir({ rir: s.targetRir, rpe: s.targetRpe }) ?? amrapStopRirOf(s);
        if (rir === null) bump(untargeted, s.targetWeight.kind);
        else bump(byRir, rir);
      }
    }
  }
  return {
    reps: {
      byReps: sortedPairs(byReps).map(([reps, sets]) => ({ reps, sets })),
      amrap,
      timed,
      open,
    },
    effort: {
      byRir: sortedPairs(byRir).map(([rir, sets]) => ({ rir, sets })),
      untargeted: sortedPairs(untargeted).map(([axis, sets]) => ({ axis, sets })),
    },
  };
};

export const amrapSets = (sessions: ReadonlyArray<ResolvedSession>): SimulatedAmrap[] =>
  sessions.flatMap((session) =>
    session.entries.flatMap((entry) =>
      entry.sets.flatMap((s, setIdx) =>
        s.amrap === true
          ? [
              {
                weekIdx: session.weekIdx,
                sessionIdx: session.sessionIdx,
                exerciseId: entry.exerciseId,
                setIdx,
                floorReps: s.targetReps,
                load: s.targetWeight,
                targetRir: effectiveRir({ rir: s.targetRir, rpe: s.targetRpe }),
                stop: s.amrapStop ?? null,
              },
            ]
          : [],
      ),
    ),
  );

/** The work of one set in seconds, or null when it cannot be told. */
const workSeconds = (s: PlannedSet, ex: Exercise | undefined): number | null => {
  if (s.work?.kind === "countdown") return s.work.durationSec;
  if (s.work?.kind === "countup") return null;
  // The logger's rule for "this exercise logs a duration" (kindNeedsDuration,
  // packages/ui/src/Logger/deriveEntryKind.ts): timed holds and cardio.
  if (ex?.isTimeBased === true || ex?.movementPattern === "cardio") return ex.defaultDurationSec ?? HOLD_FALLBACK_SEC;
  return s.targetReps > 0 ? s.targetReps * SIMULATION_SECONDS_PER_REP : null;
};

/** The order the logger visits a day's sets, with where it hops. A superset
 *  goes round-robin (nextGroupMemberEntryIdx, packages/ui/src/Logger/
 *  supersetNotebook.ts): after a set it moves to the next member with a set
 *  left, and that move is a hop (SUPERSET_REST_SEC) whenever it lands on
 *  another member; only the group's last set ends in the full rest. */
type Visit = { ei: number; si: number; hop: boolean };

const dayVisits = (session: ResolvedSession): Visit[] => {
  const out: Visit[] = [];
  const done = new Set<string>();
  session.entries.forEach((entry, ei) => {
    const gid = entry.group?.id;
    if (gid === undefined) {
      entry.sets.forEach((_, si) => out.push({ ei, si, hop: false }));
      return;
    }
    if (done.has(gid)) return;
    done.add(gid);
    const members = session.entries.flatMap((e, i) => (e.group?.id === gid ? [i] : []));
    const next = members.map(() => 0);
    let cur = 0;
    for (;;) {
      const ei2 = members[cur]!;
      out.push({ ei: ei2, si: next[cur]!, hop: false });
      next[cur] = next[cur]! + 1;
      let to = -1;
      for (let step = 1; step <= members.length; step += 1) {
        const m = (cur + step) % members.length;
        if (next[m]! < session.entries[members[m]!]!.sets.length) {
          to = m;
          break;
        }
      }
      if (to < 0) return;
      if (to !== cur) out[out.length - 1]!.hop = true;
      cur = to;
    }
  });
  return out;
};

/** One day's seconds: work, then the rest after every set but the day's last.
 *  The rest is the logger's chain (restSecondsFor), with no profile: the
 *  athlete is hypothetical.
 *  Changing implement between exercises is not counted. */
const daySeconds = (
  session: ResolvedSession,
  bySlug: ReadonlyMap<string, Exercise>,
): { seconds: number; untimed: number } => {
  let seconds = 0;
  let untimed = 0;
  const visits = dayVisits(session);
  visits.forEach(({ ei, si, hop }, vi) => {
    const entry = session.entries[ei]!;
    const s = entry.sets[si]!;
    const ex = bySlug.get(entry.exerciseId);
    const work = workSeconds(s, ex);
    if (work === null) untimed += 1;
    else seconds += work;
    if (vi === visits.length - 1) return;
    seconds += restSecondsFor({
      prescribedRestSec: s.restSec,
      supersetHop: hop,
      exerciseRestSec: ex?.defaultRestSec,
      profileRestSec: undefined,
    });
  });
  return { seconds, untimed };
};

export const simulatedDays = (
  sessions: ReadonlyArray<ResolvedSession>,
  bySlug: ReadonlyMap<string, Exercise>,
): SimulatedDay[] => {
  const days = new Map<number, SimulatedDay>();
  for (const session of sessions) {
    if (session.entries.length === 0) continue;
    const { seconds, untimed } = daySeconds(session, bySlug);
    const day = days.get(session.sessionIdx) ?? {
      sessionIdx: session.sessionIdx,
      sessionId: session.sessionId,
      sessionName: session.sessionName,
      weeks: [],
      minSeconds: seconds,
      maxSeconds: seconds,
    };
    day.weeks.push({ weekIdx: session.weekIdx, seconds, untimedSets: untimed });
    day.minSeconds = Math.min(day.minSeconds, seconds);
    day.maxSeconds = Math.max(day.maxSeconds, seconds);
    days.set(session.sessionIdx, day);
  }
  return [...days.values()].sort((a, b) => a.sessionIdx - b.sessionIdx);
};
